import React, { useState } from "react";
import useEscape from "../lib/useEscape";
import { X, MessageCircle, ShieldCheck, ArrowRight } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

// India is the default; users can switch the country code to any of these.
const COUNTRY_CODES = [
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "USA / Canada", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+971", name: "UAE", flag: "🇦🇪" },
  { code: "+966", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+65", name: "Singapore", flag: "🇸🇬" },
  { code: "+60", name: "Malaysia", flag: "🇲🇾" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+39", name: "Italy", flag: "🇮🇹" },
  { code: "+34", name: "Spain", flag: "🇪🇸" },
  { code: "+31", name: "Netherlands", flag: "🇳🇱" },
  { code: "+41", name: "Switzerland", flag: "🇨🇭" },
  { code: "+46", name: "Sweden", flag: "🇸🇪" },
  { code: "+47", name: "Norway", flag: "🇳🇴" },
  { code: "+45", name: "Denmark", flag: "🇩🇰" },
  { code: "+27", name: "South Africa", flag: "🇿🇦" },
  { code: "+20", name: "Egypt", flag: "🇪🇬" },
  { code: "+254", name: "Kenya", flag: "🇰🇪" },
  { code: "+234", name: "Nigeria", flag: "🇳🇬" },
  { code: "+55", name: "Brazil", flag: "🇧🇷" },
  { code: "+52", name: "Mexico", flag: "🇲🇽" },
  { code: "+54", name: "Argentina", flag: "🇦🇷" },
  { code: "+62", name: "Indonesia", flag: "🇮🇩" },
  { code: "+63", name: "Philippines", flag: "🇵🇭" },
  { code: "+66", name: "Thailand", flag: "🇹🇭" },
  { code: "+84", name: "Vietnam", flag: "🇻🇳" },
  { code: "+82", name: "South Korea", flag: "🇰🇷" },
  { code: "+92", name: "Pakistan", flag: "🇵🇰" },
  { code: "+880", name: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", name: "Sri Lanka", flag: "🇱🇰" },
  { code: "+977", name: "Nepal", flag: "🇳🇵" },
];

// Magic phone number that, instead of sending an OTP, swaps the modal into the
// admin-login form. The user clicks Send-OTP-on-WhatsApp first, then the flip
// happens (no actual OTP is dispatched for this number).
const ADMIN_TRIGGER_PHONES = new Set(["9825025010"]);

export default function AuthModal({ open, onClose }) {
  useEscape(open, onClose);
  const { login } = useAuth();
  const [view, setView] = useState("phone"); // phone | otp | admin
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [adminU, setAdminU] = useState("");
  const [adminP, setAdminP] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => {
    setView("phone"); setCountryCode("+91"); setPhone(""); setOtp(""); setName(""); setAdminU(""); setAdminP("");
  };

  const sendOtp = async () => {
    if (!phone) return toast.error("Enter your mobile number");
    // Magic phone → flip to admin login form (no OTP sent).
    if (ADMIN_TRIGGER_PHONES.has(phone)) {
      setView("admin");
      return;
    }
    if (!/^\d{6,15}$/.test(phone)) return toast.error("Enter a valid mobile number");
    setLoading(true);
    try {
      const fullPhone = countryCode === "+91" ? phone : `${countryCode}${phone}`;
      const res = await api.post("/auth/otp/send", { phone_number: fullPhone });
      toast.success(res.data.message || "OTP sent on WhatsApp");
      setView("otp");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send OTP"));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setLoading(true);
    try {
      const fullPhone = countryCode === "+91" ? phone : `${countryCode}${phone}`;
      const res = await api.post("/auth/otp/verify", { phone_number: fullPhone, otp, name });
      login(res.data.token, res.data.user);
      toast.success("Signed in!");
      onClose();
      reset();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Invalid OTP"));
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/admin/login", { username: adminU, password: adminP });
      login(res.data.token, res.data.user);
      toast.success("Welcome, admin!");
      onClose();
      reset();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Invalid admin credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-md p-4" onClick={onClose}>
      <div
        className="bg-card w-full max-w-md overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Sign in to BHEGA"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <div className="eyebrow text-xs">{view === "admin" ? "Access" : "Welcome"}</div>
            <h3 className="font-heading font-extrabold text-2xl mt-0.5 text-foreground">
              {view === "admin" ? "Admin sign in" : "Sign in to BHEGA"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-foreground/10 rounded-full text-muted-foreground hover:text-foreground transition-all" data-testid="auth-modal-close">
            <X size={18} />
          </button>
        </div>

        {view === "phone" && (
          <div className="px-6 py-6 space-y-4" data-testid="auth-view-phone">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <MessageCircle size={14} className="text-brand" />
              We&apos;ll WhatsApp you a verification code.
            </p>
            <div>
              <label className="eyebrow text-xs">Mobile</label>
              <div className="flex mt-2 gap-2">
                <select
                  data-testid="country-code-select"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="input-bhega text-sm font-semibold max-w-[120px]"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  data-testid="phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
                  placeholder={countryCode === "+91" ? "98xxxxxxxx" : "Mobile number"}
                  className="input-bhega flex-1"
                  maxLength={15}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel-national"
                />
              </div>
            </div>
            <div>
              <label className="eyebrow text-xs">Your name (optional)</label>
              <input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Elon Musk"
                className="input-bhega mt-2"
              />
            </div>
            <button
              data-testid="send-otp-btn"
              disabled={loading}
              onClick={sendOtp}
              className="btn-primary w-full"
            >
              {loading ? "Sending..." : "Send OTP on WhatsApp"} <ArrowRight size={16} />
            </button>
          </div>
        )}

        {view === "otp" && (
          <div className="px-6 py-6 space-y-4" data-testid="auth-view-otp">
            <p className="text-sm text-muted-foreground">Enter the 6-digit OTP sent to <strong className="text-foreground">{countryCode} {phone}</strong></p>
            <input
              data-testid="otp-input"
              value={otp}
              // onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onChange={(e) => setOtp(e.target.value.toUpperCase().replace(/[^A-Za-z0-9]/g, "").slice(0, 6))}
              placeholder="00A3B7"
              maxLength={6}
              inputMode="text"
              //alphanumeric, autocapitalize, no spaces
              pattern="[A-Za-z0-9]{6}"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              //pattern="[0-9]*"
              autoComplete="one-time-code"
              className="input-bhega text-center text-3xl tracking-[0.5em] font-extrabold font-heading"
            />
            <button
              data-testid="verify-otp-btn"
              disabled={loading}
              onClick={verifyOtp}
              className="btn-primary w-full"
            >
              {loading ? "Verifying..." : "Verify & continue"}
            </button>
            <button
              data-testid="otp-change-number"
              onClick={() => setView("phone")}
              className="text-xs underline text-muted-foreground hover:text-foreground transition-colors"
            >
              Change number
            </button>
          </div>
        )}

        {view === "admin" && (
          <div className="px-6 py-6 space-y-4" data-testid="auth-view-admin">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <ShieldCheck size={14} className="text-brand" />
              Admin access — sign in with your administrator credentials.
            </p>
            <div>
              <label className="eyebrow text-xs">Username</label>
              <input data-testid="admin-username" value={adminU} onChange={(e) => setAdminU(e.target.value)} className="input-bhega mt-2" />
            </div>
            <div>
              <label className="eyebrow text-xs">Password</label>
              <input data-testid="admin-password" type="password" value={adminP} onChange={(e) => setAdminP(e.target.value)} className="input-bhega mt-2" />
            </div>
            <button data-testid="admin-login-btn" disabled={loading} onClick={adminLogin} className="btn-primary w-full">
              {loading ? "Signing in..." : "Sign in as Admin"}
            </button>
            <button
              data-testid="admin-back-to-user"
              onClick={() => setView("phone")}
              className="text-xs underline text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to buyer sign-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
